// Extracted from original/src/App.tsx:35369-36265 (the `activeTab === "wallets"` block, ADR-08 §2.1):
//   vaultSection         :35430-35583 (vault cards :35451-35508; employee dinar / dollar sub-safe cards :35513-35578)
//   digitalBoxesSection  :35584-35738 (box cards with the `isJustCharged` highlight :35600-35613, "كشف" / "شحن" :35704-35732)
//   transferFormSection  :35739-36249 (tab switch :35751-35797; local tab from / to dropdowns :35802-36024; office tab
//                         PartnerOfficeSelector + destination dropdown :36026-36137; amount :36139-36158; commission +
//                         financial-impact box :36160-36212; note :36214-36229; submit :36230-36246)
//   layout               :36250-36264 (motion.div, 3-column grid)
// Kept: every class string, the four dropdown open flags (`showFromDropdown`, `showToDropdown`,
// `showPartnerOfficeDropdown`, `showToOfficeDropdown`, pure UI state), icons and sizes, the `$` / `د.ع` unit markup.
// Cut: the permission / visibility filter `visibleVaults` and `showVaultSection` (:35371-35429, container →
// `vaults`, `showVaultSection`); `subSafeBalances` / `usersList` / `availableSubSafesUsers` (:35376-35400 →
// `custody`, `custodyOptions`); `currencies.reduce(...)` for the currency vault total (:35443-35448) and every
// `formatNumber(...)` → preformatted labels (ADR-02 §2.9); `getWalletOrSubSafeLabel` / `getTransferToLabel`
// (:2319-2352 → `fromLabel` / `toLabel`, formatted by the container, including the "اختر المصدر" / "اختر الوجهة"
// defaults); the tab-switch field resets (:35754-35762, :35776-35785 → `onModeChange`); `showToast` + `setTransferTo("")`
// on source = destination (:35847-35857 etc. → `onSameAccountWarning`); `parseNumeric(...) > 0` gate and the
// amount + commission sum of the impact box (:36184-36206 → `impact` object, null = hidden); the submit guard
// (:36232-36237 → `canSubmit`); `handleTransfer` → `onSubmit`. Removed by decision: the `w.type === "manual"` branches
// (:35597, :35608, :35644, :35671, :35691, :35697-35701, :35723; the manual box type no longer exists, D-077) — the digital
// branch's classes and the `Grip` icon are kept; the old numeric ids / name matching (`w.id === 11`, "خزينة الدولار",
// :35403-35413, :35439-35442, :35460) → `kind` prop. Added: data-testid attributes.
import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeftRight, ChevronDown, Grip, History, Plus, ShieldCheck } from 'lucide-react';
import { NumericInput } from '../components/NumericInput.js';
import { PartnerOfficeSelector, type PartnerOfficeOption, type PartnerOfficeCounts } from '../components/PartnerOfficeSelector.js';
import { useT } from '../i18n-context.js';

export type WalletsVaultKind = 'vault' | 'dollar_vault' | 'currency_vault';

export interface WalletsVault {
  readonly id: string;
  readonly kind: WalletsVaultKind;
  readonly name: string;
  /** Formatted by the container: USD figure for `dollar_vault`, IQD figure (currency total in IQD for `currency_vault`). */
  readonly balanceLabel: string;
}

/** The two employee sub-safe cards (App.tsx:35510-35580); null = sub-safes off or no permission. */
export interface WalletsCustody {
  readonly username: string;
  /** Formatted by the container. */
  readonly iqdLabel: string;
  /** Formatted by the container. */
  readonly usdLabel: string;
  /** Old gate `hasPermission(currentUser, "dollar")` (:35546). */
  readonly showUsd: boolean;
  readonly onViewDetails: () => void;
}

export interface WalletsBox {
  readonly id: string;
  readonly name: string;
  /** Old `formatNumber(balance - pending_commissions, 0)` (:35650-35654), formatted by the container. */
  readonly withoutCommissionLabel: string;
  /** Old `formatNumber(balance, 0)` (:35677), formatted by the container. */
  readonly withCommissionLabel: string;
  /** Old `w.id === justChargedWalletId` (:35600). */
  readonly justCharged?: boolean;
}

export interface WalletsTransferOption {
  readonly id: string;
  /** Main group: the box / vault name. Sub-safe group: the username (rendered through `wallets.sub_safe_of`). */
  readonly label: string;
  /** Formatted by the container. */
  readonly balanceLabel: string;
}

export type WalletsTransferMode = 'wallet' | 'office';

export interface WalletsTransferForm {
  readonly mode: WalletsTransferMode;
  readonly fromId: string;
  readonly toId: string;
  readonly amount: string;
  readonly commission: string;
  readonly note: string;
  readonly partnerId: string;
  readonly partnerSearch: string;
}

/** The "📊 الأثر المالي" box (App.tsx:36184-36210); every figure formatted by the container. */
export interface WalletsImpact {
  readonly amountLabel: string;
  readonly commissionLabel: string;
  readonly totalLabel: string;
}

export interface WalletsProps {
  readonly vaults: readonly WalletsVault[];
  readonly custody: WalletsCustody | null;
  readonly showVaultSection: boolean;
  readonly boxes: readonly WalletsBox[];
  readonly onVaultStatement: (id: string) => void;
  readonly onViewCurrencyVault: () => void;
  /** Click on a box figure (old `handleShowWalletHistory`, :35637-35642). */
  readonly onBoxHistory: (id: string) => void;
  /** The "كشف" button (old `openWalletFilterModal`, :35706-35712). */
  readonly onBoxStatement: (id: string) => void;
  readonly onCharge: (id: string) => void;

  readonly form: WalletsTransferForm;
  readonly onFormChange: (patch: Partial<WalletsTransferForm>) => void;
  /** The container resets the form fields on a tab switch (old :35754-35762, :35776-35785). */
  readonly onModeChange: (mode: WalletsTransferMode) => void;
  /** "الصناديق والخزنات الرئيسية" group. */
  readonly transferOptions: readonly WalletsTransferOption[];
  /** "الخزنات الفرعية للموظفين" group; empty = group hidden (old `companySettings.sub_safes_active`). */
  readonly custodyOptions: readonly WalletsTransferOption[];
  readonly partnerOptions: readonly PartnerOfficeOption[];
  readonly selectedPartner: PartnerOfficeOption | null;
  readonly partnerCounts?: PartnerOfficeCounts;
  /** Labels of the selected source / destination as the old `getWalletOrSubSafeLabel` / `getTransferToLabel` returned them. */
  readonly fromLabel: string;
  readonly toLabel: string;
  readonly impact: WalletsImpact | null;
  readonly canSubmit: boolean;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  /** Source = destination picked (old toast + clear destination, :35847-35857); the container shows the toast. */
  readonly onSameAccountWarning: () => void;
}

const TRIGGER = "w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-3 md:px-6 py-2.5 md:py-4 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm md:text-base lg:text-lg dark:text-white flex justify-between items-center shadow-inner text-right";
const FIELD_LABEL = "block text-[9px] sm:text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 mb-2 uppercase tracking-wide";
const NUMERIC_INPUT = "w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-3 md:px-6 py-2.5 md:py-4 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm md:text-base lg:text-lg dark:text-white appearance-none shadow-inner font-mono";

export function Wallets(props: WalletsProps) {
  const t = useT();
  const { form } = props;
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [showPartnerOfficeDropdown, setShowPartnerOfficeDropdown] = useState(false);
  const [showToOfficeDropdown, setShowToOfficeDropdown] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!props.busy && props.canSubmit) props.onSubmit();
  };

  const vaultSection = (
    <div className="premium-card p-8 relative overflow-hidden" data-testid="wallets-vault-section">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full -mr-16 -mt-16 blur-2xl transition-all duration-700" />
      <h3 className="text-2xl font-black mb-8 flex items-center gap-3 dark:text-white uppercase tracking-tight relative z-10">
        <ShieldCheck className="text-primary-600 dark:text-primary-400" />
        {t('vault')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10 w-full">
        {props.vaults.map((w) => {
          const isCurrVault = w.kind === 'currency_vault';

          return (
            <div
              key={w.id}
              data-testid={`vault-card-${w.id}`}
              className="bg-primary-50/50 dark:bg-primary-900/10 p-5 rounded-2xl flex flex-col justify-between gap-4 group border border-primary-100/50 dark:border-primary-500/10 shadow-inner"
            >
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-1">
                  {w.name}
                </span>
                <div className="flex flex-col gap-1">
                  {w.kind === 'dollar_vault' ? (
                    <span className="text-3xl font-black text-slate-900 dark:text-white font-mono digital-glow">
                      <span className="text-lg font-bold ml-1">
                        $
                      </span>
                      {w.balanceLabel}
                    </span>
                  ) : isCurrVault ? (
                    <span className="text-3xl font-black text-slate-900 dark:text-white font-mono digital-glow">
                      {w.balanceLabel}{" "}
                      <span className="text-lg">د.ع</span>
                    </span>
                  ) : (
                    <>
                      <span className="text-3xl font-black text-slate-900 dark:text-white font-mono digital-glow">
                        {w.balanceLabel}{" "}
                        <span className="text-lg">د.ع</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-2 w-full pt-3 border-t border-primary-200/20 dark:border-primary-500/5">
                {isCurrVault ? (
                  <button
                    onClick={() => props.onViewCurrencyVault()}
                    data-testid={`vault-view-currencies-${w.id}`}
                    className="w-full bg-primary-600 text-white py-3 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 hover:bg-primary-500 transition-all shadow-md shadow-primary-500/10 uppercase tracking-wider active:scale-95"
                  >
                    <History size={14} />
                    {t('wallets.view_currency_vault')}
                  </button>
                ) : (
                  <button
                    onClick={() => props.onVaultStatement(w.id)}
                    data-testid={`vault-statement-${w.id}`}
                    className="w-full bg-primary-600 text-white py-3 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 hover:bg-primary-500 transition-all shadow-md shadow-primary-500/10 uppercase tracking-wider active:scale-95 cursor-pointer"
                  >
                    <History size={14} />
                    {t('wallets.movements')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {props.custody && (
          <>
            {/* Employee Dinar Sub-Safe Card */}
            <div className="bg-blue-50/50 dark:bg-blue-950/20 p-5 rounded-2xl flex flex-col justify-between gap-4 group border border-blue-100/50 dark:border-blue-500/10 shadow-inner" data-testid="custody-card-iqd">
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1">
                  {t('wallets.my_dinar_sub_safe', { username: props.custody.username })}
                </span>
                <div className="flex flex-col gap-1">
                  <span className="text-3xl font-black text-slate-900 dark:text-white font-mono digital-glow">
                    {props.custody.iqdLabel}{" "}
                    <span className="text-lg font-bold">
                      د.ع
                    </span>
                  </span>
                </div>
              </div>
              <div className="flex gap-2 w-full pt-3 border-t border-blue-200/20 dark:border-blue-500/5">
                <button
                  onClick={props.custody.onViewDetails}
                  data-testid="custody-view-iqd"
                  className="w-full bg-blue-600 text-white py-3 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 hover:bg-blue-500 transition-all shadow-md shadow-blue-500/10 uppercase tracking-wider active:scale-95"
                >
                  <ArrowLeftRight size={14} />
                  {t('wallets.view_custody_details')}
                </button>
              </div>
            </div>
            {/* Employee Dollar Sub-Safe Card */}
            {props.custody.showUsd && (
              <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-5 rounded-2xl flex flex-col justify-between gap-4 group border border-emerald-100/50 dark:border-emerald-500/10 shadow-inner" data-testid="custody-card-usd">
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-1">
                    {t('wallets.my_dollar_sub_safe', { username: props.custody.username })}
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="text-3xl font-black text-slate-900 dark:text-white font-mono digital-glow">
                      <span className="text-lg font-bold ml-1">
                        $
                      </span>
                      {props.custody.usdLabel}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 w-full pt-3 border-t border-emerald-200/20 dark:border-emerald-500/5">
                  <button
                    onClick={props.custody.onViewDetails}
                    data-testid="custody-view-usd"
                    className="w-full bg-emerald-600 text-white py-3 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 hover:bg-emerald-500 transition-all shadow-md shadow-emerald-500/10 uppercase tracking-wider active:scale-95"
                  >
                    <ArrowLeftRight size={14} />
                    {t('wallets.view_custody_details')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const digitalBoxesSection = (
    <div
      id="digital-boxes-section"
      data-testid="wallets-boxes-section"
      className="premium-card p-8 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 rounded-full -mr-16 -mt-16 blur-2xl transition-all duration-700" />
      <h3 className="text-2xl font-black mb-8 flex items-center gap-3 dark:text-white uppercase tracking-tight relative z-10">
        <Grip className="text-orange-600 dark:text-orange-400" />
        {t('digital')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
        {props.boxes.map((w) => {
          const isJustCharged = w.justCharged === true;
          return (
            <div
              key={w.id}
              id={`wallet-box-${w.id}`}
              data-testid={`wallet-card-${w.id}`}
              className={`p-6 rounded-[1.5rem] border flex flex-col gap-4 group transition-all duration-300 shadow-inner ${
                isJustCharged
                  ? "bg-orange-500/10 dark:bg-orange-950/20 border-orange-500 dark:border-orange-400 ring-4 ring-orange-500/35 shadow-lg shadow-orange-500/20 scale-[1.02]"
                  : "bg-slate-50/50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800 hover:border-orange-500/50"
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-bold uppercase tracking-widest mb-1 ${
                        isJustCharged
                          ? "text-orange-600 dark:text-orange-400 font-extrabold"
                          : "text-slate-400 dark:text-slate-500"
                      }`}
                    >
                      {w.name}
                    </span>
                    {isJustCharged && (
                      <span className="px-2 py-0.5 bg-orange-500 text-white text-[8px] font-black uppercase rounded-full tracking-wider animate-pulse">
                        {t('wallets.just_updated')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <span
                        onClick={() => props.onBoxHistory(w.id)}
                        data-testid={`wallet-balance-excl-${w.id}`}
                        className="text-2xl font-black font-mono digital-glow cursor-pointer hover:underline text-orange-600 dark:text-orange-400"
                      >
                        {w.withoutCommissionLabel}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                        {t('wallets.excl_comm')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        onClick={() => props.onBoxHistory(w.id)}
                        data-testid={`wallet-balance-incl-${w.id}`}
                        className="text-lg font-black font-mono digital-glow cursor-pointer hover:underline opacity-80 text-emerald-600 dark:text-emerald-400"
                      >
                        {w.withCommissionLabel}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                        {t('wallets.incl_comm')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center border bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-orange-100/50 dark:border-orange-500/10"
                >
                  <Grip size={20} />
                </div>
              </div>
              <div className="flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  onClick={() => props.onBoxStatement(w.id)}
                  data-testid={`wallet-statement-${w.id}`}
                  className="flex-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 py-2 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all border border-primary-100 dark:border-primary-500/20 uppercase tracking-widest cursor-pointer"
                  title={t('wallets.log_title')}
                >
                  <History size={14} />
                  {t('wallets.log')}
                </button>
                <button
                  onClick={() => props.onCharge(w.id)}
                  data-testid={`wallet-charge-${w.id}`}
                  className="flex-1 bg-orange-600 hover:bg-orange-500 text-white py-2 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 shadow-lg shadow-orange-600/20 uppercase tracking-widest transition-all active:scale-95"
                >
                  <Plus size={14} />
                  {t('wallets.charge')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const pickFrom = (id: string) => {
    props.onFormChange({ fromId: id });
    setShowFromDropdown(false);
    if (id === form.toId) {
      props.onSameAccountWarning();
    }
  };
  const pickTo = (id: string) => {
    props.onFormChange({ toId: id });
    setShowToDropdown(false);
    if (id === form.fromId) {
      props.onSameAccountWarning();
    }
  };

  const transferFormSection = (
    <div
      id="transfer-form"
      data-testid="wallets-transfer-form"
      className="premium-card p-8 h-fit relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 blur-2xl transition-all duration-700" />
      <h3 className="text-2xl font-black mb-6 flex items-center gap-3 dark:text-white uppercase tracking-tight relative z-10">
        <ArrowLeftRight className="text-emerald-600 dark:text-emerald-400" />
        {t('wallets.funding_title')}
      </h3>
      <div className="flex rounded-xl p-1 bg-slate-100 dark:bg-slate-900/40 border border-slate-200/50 dark:border-slate-800/50 mb-6 relative z-10">
        <button
          type="button"
          data-testid="transfer-tab-wallet"
          onClick={() => props.onModeChange('wallet')}
          className={`flex-1 py-2.5 text-xs font-black rounded-lg transition-all duration-300 ${
            form.mode === "wallet"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs"
              : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400"
          }`}
        >
          {t('wallets.tab_local')}
        </button>
        <button
          type="button"
          data-testid="transfer-tab-office"
          onClick={() => props.onModeChange('office')}
          className={`flex-1 py-2.5 text-xs font-black rounded-lg transition-all duration-300 ${
            form.mode === "office"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs"
              : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400"
          }`}
        >
          {t('wallets.tab_office')}
        </button>
      </div>
      <form
        onSubmit={submit}
        className="space-y-6 relative z-10 w-full"
      >
        {form.mode === "wallet" ? (
          <>
            <div>
              <label className={FIELD_LABEL}>
                {t('from')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="transfer-from"
                  onClick={() => {
                    setShowFromDropdown(!showFromDropdown);
                    setShowToDropdown(false);
                    setShowPartnerOfficeDropdown(false);
                    setShowToOfficeDropdown(false);
                  }}
                  className={TRIGGER}
                >
                  <span className="truncate">
                    {props.fromLabel}
                  </span>
                  <ChevronDown
                    size={18}
                    className="text-gray-400 shrink-0"
                  />
                </button>
                {showFromDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowFromDropdown(false)}
                    />
                    <div className="absolute z-50 mt-2 w-full bg-white dark:bg-[#161b22] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-h-60 overflow-y-auto text-xs font-sans text-right" data-testid="transfer-from-dropdown">
                      <div className="px-3 py-2 bg-slate-50 dark:bg-[#0d1117] text-slate-400 dark:text-slate-500 font-black border-b border-slate-100 dark:border-slate-800 tracking-wider">
                        {t('wallets.group_main')}
                      </div>
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {props.transferOptions.map((w) => (
                          <button
                            key={w.id}
                            type="button"
                            data-testid={`transfer-from-option-${w.id}`}
                            onClick={() => pickFrom(w.id)}
                            className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/50 transition-colors font-semibold ${form.fromId === w.id ? "bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-200"}`}
                          >
                            {w.label} (
                            {w.balanceLabel})
                          </button>
                        ))}
                      </div>
                      {props.custodyOptions.length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-slate-50 dark:bg-[#0d1117] text-slate-400 dark:text-slate-500 font-black border-y border-slate-100 dark:border-slate-800 tracking-wider">
                            {t('wallets.group_sub_safes')}
                          </div>
                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {props.custodyOptions.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`transfer-from-option-${u.id}`}
                                onClick={() => pickFrom(u.id)}
                                className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/50 transition-colors font-semibold ${form.fromId === u.id ? "bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-200"}`}
                              >
                                {t('wallets.sub_safe_of', { username: u.label })}{" "}
                                - {u.balanceLabel}{" "}
                                د.ع
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div>
              <label className={FIELD_LABEL}>
                {t('to')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="transfer-to"
                  onClick={() => {
                    setShowToDropdown(!showToDropdown);
                    setShowFromDropdown(false);
                    setShowPartnerOfficeDropdown(false);
                    setShowToOfficeDropdown(false);
                  }}
                  className={TRIGGER}
                >
                  <span className="truncate">
                    {props.toLabel}
                  </span>
                  <ChevronDown
                    size={18}
                    className="text-gray-400 shrink-0"
                  />
                </button>
                {showToDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowToDropdown(false)}
                    />
                    <div className="absolute z-50 mt-2 w-full bg-white dark:bg-[#161b22] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-h-60 overflow-y-auto text-xs font-sans text-right" data-testid="transfer-to-dropdown">
                      <div className="px-3 py-2 bg-slate-50 dark:bg-[#0d1117] text-slate-400 dark:text-slate-500 font-black border-b border-slate-100 dark:border-slate-800 tracking-wider">
                        {t('wallets.group_main')}
                      </div>
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {props.transferOptions.map((w) => (
                          <button
                            key={w.id}
                            type="button"
                            data-testid={`transfer-to-option-${w.id}`}
                            onClick={() => pickTo(w.id)}
                            className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/50 transition-colors font-semibold ${form.toId === w.id ? "bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-200"}`}
                          >
                            {w.label} (
                            {w.balanceLabel})
                          </button>
                        ))}
                      </div>
                      {props.custodyOptions.length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-slate-50 dark:bg-[#0d1117] text-slate-400 dark:text-slate-500 font-black border-y border-slate-100 dark:border-slate-800 tracking-wider">
                            {t('wallets.group_sub_safes')}
                          </div>
                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {props.custodyOptions.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`transfer-to-option-${u.id}`}
                                onClick={() => pickTo(u.id)}
                                className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/50 transition-colors font-semibold ${form.toId === u.id ? "bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-200"}`}
                              >
                                {t('wallets.sub_safe_of', { username: u.label })}{" "}
                                - {u.balanceLabel}{" "}
                                د.ع
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className={FIELD_LABEL}>
                {t('wallets.partner_label')}
              </label>
              <PartnerOfficeSelector
                options={props.partnerOptions}
                selected={props.selectedPartner}
                selectedId={form.partnerId}
                onSelect={(id) => props.onFormChange({ partnerId: id })}
                {...(props.partnerCounts ? { counts: props.partnerCounts } : {})}
                searchQuery={form.partnerSearch}
                onSearchChange={(q) => props.onFormChange({ partnerSearch: q })}
                isOpen={showPartnerOfficeDropdown}
                setIsOpen={setShowPartnerOfficeDropdown}
                onCloseOtherDropdowns={() => {
                  setShowFromDropdown(false);
                  setShowToDropdown(false);
                  setShowToOfficeDropdown(false);
                }}
              />
            </div>

            <div>
              <label className={FIELD_LABEL}>
                {t('wallets.destination_label')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="transfer-to-office"
                  onClick={() => {
                    setShowToOfficeDropdown(!showToOfficeDropdown);
                    setShowFromDropdown(false);
                    setShowToDropdown(false);
                    setShowPartnerOfficeDropdown(false);
                  }}
                  className={`${TRIGGER} cursor-pointer`}
                >
                  <span className="truncate">
                    {props.toLabel ||
                      t('wallets.select_destination')}
                  </span>
                  <ChevronDown
                    size={18}
                    className="text-gray-400 shrink-0"
                  />
                </button>
                {showToOfficeDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowToOfficeDropdown(false)}
                    />
                    <div className="absolute z-50 mt-2 w-full bg-white dark:bg-[#161b22] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-h-60 overflow-y-auto text-xs font-sans text-right" data-testid="transfer-to-office-dropdown">
                      <div className="px-3 py-2 bg-slate-50 dark:bg-[#0d1117] text-slate-400 dark:text-slate-500 font-black border-b border-slate-100 dark:border-slate-800 tracking-wider">
                        {t('wallets.group_main')}
                      </div>
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {props.transferOptions.map((w) => (
                          <button
                            key={w.id}
                            type="button"
                            data-testid={`transfer-to-office-option-${w.id}`}
                            onClick={() => {
                              props.onFormChange({ toId: w.id });
                              setShowToOfficeDropdown(false);
                            }}
                            className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/50 transition-colors font-semibold cursor-pointer ${form.toId === w.id ? "bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-200"}`}
                          >
                            {w.label} ({w.balanceLabel})
                          </button>
                        ))}
                      </div>
                      {props.custodyOptions.length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-slate-50 dark:bg-[#0d1117] text-slate-400 dark:text-slate-500 font-black border-y border-slate-100 dark:border-slate-800 tracking-wider">
                            {t('wallets.group_sub_safes')}
                          </div>
                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {props.custodyOptions.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`transfer-to-office-option-${u.id}`}
                                onClick={() => {
                                  props.onFormChange({ toId: u.id });
                                  setShowToOfficeDropdown(false);
                                }}
                                className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/50 transition-colors font-semibold cursor-pointer ${form.toId === u.id ? "bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-200"}`}
                              >
                                {t('wallets.sub_safe_of', { username: u.label })}{" "}
                                - {u.balanceLabel} د.ع
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-[9px] sm:text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wide">
              {form.mode === "office"
                ? t('wallets.funding_amount')
                : t('amountIQD')}
            </label>
            {form.mode === "office" && (
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                {t('wallets.enters_box')}
              </span>
            )}
          </div>
          <NumericInput
            value={form.amount ?? ""}
            onChange={(amount) => props.onFormChange({ amount })}
            className={NUMERIC_INPUT}
            placeholder="0"
          />
        </div>

        {form.mode === "office" && (
          <>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-[9px] sm:text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                  {t('wallets.commission_label')}
                </label>
                <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                  {t('wallets.direct_expense')}
                </span>
              </div>
              <NumericInput
                value={form.commission ?? ""}
                onChange={(commission) => props.onFormChange({ commission })}
                className={NUMERIC_INPUT}
                placeholder="0"
              />
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5 font-medium leading-normal">
                {t('wallets.commission_hint')}
              </p>
            </div>

            {props.impact && (
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/80 space-y-2.5 text-xs font-bold shadow-xs" data-testid="transfer-impact">
                <div className="text-[11px] font-black text-slate-500 dark:text-slate-400 border-b border-slate-200/70 dark:border-slate-800 pb-2">
                  {t('wallets.impact_title')}
                </div>
                <div className="flex justify-between items-center text-slate-600 dark:text-slate-300">
                  <span>{t('wallets.impact_received')}</span>
                  <span className="font-mono text-emerald-600 dark:text-emerald-400 font-black">
                    +{props.impact.amountLabel} د.ع
                  </span>
                </div>
                <div className="flex justify-between items-center text-slate-600 dark:text-slate-300">
                  <span>{t('wallets.impact_commission')}</span>
                  <span className="font-mono text-amber-600 dark:text-amber-400 font-black">
                    +{props.impact.commissionLabel} د.ع
                  </span>
                </div>
                <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center font-black text-slate-900 dark:text-white">
                  <span>
                    {t('wallets.impact_total')}
                  </span>
                  <span className="font-mono text-primary-600 dark:text-primary-400 text-sm">
                    {props.impact.totalLabel} د.ع
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <div>
          <label className={FIELD_LABEL}>
            {t('note')}
          </label>
          <input
            type="text"
            data-testid="transfer-note"
            value={form.note || ""}
            onChange={(e) => props.onFormChange({ note: e.target.value })}
            className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-3 md:px-6 py-2.5 md:py-4 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm md:text-base lg:text-lg dark:text-white appearance-none shadow-inner"
            placeholder={t('wallets.note_placeholder')}
          />
        </div>
        <button
          type="submit"
          data-testid="transfer-submit"
          disabled={props.busy || !props.canSubmit}
          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-4 rounded-2xl text-xs md:text-sm uppercase tracking-widest transition-all duration-300 shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
        >
          <ArrowLeftRight size={18} />
          {props.busy
            ? t('processing')
            : t('wallets.confirm_transfer')}
        </button>
      </form>
    </div>
  );

  return (
    <motion.div
      key="wallets_tab_content"
      data-testid="wallets-screen"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="grid grid-cols-1 lg:grid-cols-3 gap-8"
    >
      <div className="lg:col-span-2 space-y-8">
        {props.showVaultSection && vaultSection}
        {digitalBoxesSection}
      </div>
      <div className="lg:col-span-1">{transferFormSection}</div>
    </motion.div>
  );
}
